/**
 * @file Reads an `invoiceList` answer into the Invoicing tab's invoice rows,
 * one per invoice in server order, with the server's uncapped
 * `totalInvoices` the "Showing N of M" caption needs and the period's `to`
 * that AGE counts up to. Consumer: `AnalyticsInvoicingPanel`. The invoiced
 * and outstanding amounts follow the granted level through
 * `analyticsInvoiceAmount`; with the bucket they feed `analyticsInvoiceStatus`,
 * and the served `invoiceDueDate` feeds `analyticsDaysPastDue`. `bucket` is
 * null both for an invoice that is not outstanding and for one with no due
 * date. An absent or loading answer is loading, a refused or failed one is
 * refused, and any malformed row fails the whole model with a message.
 */
import { DateTime } from 'luxon';
import type { Snapshot } from 'valtio';

import { analyticsDecimalFraction } from '#types';
import type { NamedQueryCell } from '#types';

import { analyticsAgingBucketCell } from './analyticsAgingBucketCell';
import { analyticsInvoiceAmount } from './analyticsInvoiceAmount';
import { analyticsMalformedAnswer } from './analyticsMalformedAnswer';
import { analyticsRefusedColumn } from './analyticsRefusedColumn';
import { analyticsUnansweredFigure } from './analyticsUnansweredFigure';
import type {
  AnalyticsInvoiceAmount,
  AnalyticsInvoiceRow,
  AnalyticsInvoices,
} from './analyticsInvoicingTab.types';
import type { AnalyticsAnswer } from './index.types';

const decimalOrNull = (
  cell: NamedQueryCell | undefined,
): cell is string | null =>
  cell === null ||
  (typeof cell === 'string' && analyticsDecimalFraction(cell) !== undefined);

const invoiceRow = (
  row: Snapshot<Record<string, NamedQueryCell>>,
  amount: AnalyticsInvoiceAmount,
): AnalyticsInvoiceRow | undefined => {
  const { formId, invoiceNumber, projectName, invoiceDueDate } = row;
  const value = row[amount.invoiced];
  const outstanding = row[amount.outstanding];
  const bucket = analyticsAgingBucketCell(row.bucket);
  const valid =
    typeof formId === 'string' &&
    typeof invoiceNumber === 'string' &&
    (projectName === null || typeof projectName === 'string') &&
    (invoiceDueDate === null ||
      (typeof invoiceDueDate === 'string' &&
        DateTime.fromSQL(invoiceDueDate).isValid)) &&
    bucket !== undefined &&
    decimalOrNull(value) &&
    decimalOrNull(outstanding);
  return valid
    ? {
        formId,
        invoiceNumber,
        projectName,
        value,
        outstanding,
        invoiceDueDate,
        bucket,
      }
    : undefined;
};

export const analyticsInvoices = (
  answer: Snapshot<AnalyticsAnswer> | undefined,
): AnalyticsInvoices => {
  if (answer?.status !== 'answered') return analyticsUnansweredFigure(answer);
  const { result } = answer;
  const amount = analyticsInvoiceAmount(result.level);
  const rows = result.rows.map((row) => invoiceRow(row, amount));
  const parsed = rows.filter(
    (row): row is AnalyticsInvoiceRow => row !== undefined,
  );
  if (parsed.length !== rows.length)
    return analyticsMalformedAnswer('invoiceList');
  const [first] = result.rows;
  const totalInvoices = first?.totalInvoices ?? 0;
  if (typeof totalInvoices !== 'number' || !Number.isInteger(totalInvoices))
    return analyticsMalformedAnswer('invoiceList');
  return {
    status: 'answered',
    rows: parsed,
    totalInvoices,
    to: result.period.to,
    amount,
    refusedValue: analyticsRefusedColumn(result, amount.invoiced),
    refusedOutstanding: analyticsRefusedColumn(result, amount.outstanding),
    refusedProjectName: analyticsRefusedColumn(result, 'projectName'),
    refusedInvoiceDueDate: analyticsRefusedColumn(result, 'invoiceDueDate'),
    refusedBucket: analyticsRefusedColumn(result, 'bucket'),
  };
};
